import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, ShoppingCart, X, Lock, PackageSearch } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CART_KEY = 'genora_wholesale_cart';
const CAT_COLORS = {
  'Weight Loss': '#0f766e', 'Recovery': '#b45309', 'Growth Hormone': '#3730a3',
  'Skin': '#be185d', 'Cognitive': '#1e293b', 'Longevity': '#4d7c0f', 'Solvents': '#0369a1',
};

// Google Drive share links (…/file/d/<id>/view, open?id=…, uc?id=…) return an HTML
// page, not an image, so <img> breaks. Convert them to a direct thumbnail URL.
export const normalizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const u = url.trim();
  if (u.includes('drive.google.com')) {
    const m = u.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || u.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return `https://drive.google.com/thumbnail?id=${m[1]}&sz=w1000`;
  }
  if (u.startsWith('/')) return `${API_URL}${u}`;
  return u;
};

const CatalogImage = ({ product }) => {
  const [failed, setFailed] = React.useState(false);
  const src = normalizeImageUrl(product.image_url);
  if (!src || failed) {
    return (
      <div className="text-slate-300 text-xs text-center" data-testid={`catalog-img-fallback-${product.id}`}>
        <PackageSearch className="h-8 w-8 mx-auto mb-1" />{product.code}
      </div>
    );
  }
  return <img src={src} alt={product.name} className="max-h-24 object-contain" onError={() => setFailed(true)} data-testid={`catalog-img-${product.id}`} />;
};

const CatalogPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = React.useState('loading'); // loading | denied | ready
  const [products, setProducts] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [categories, setCategories] = React.useState([]);
  const [activeCat, setActiveCat] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('featured');
  const [availOnly, setAvailOnly] = React.useState(false);
  const [cart, setCart] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = React.useState(false);
  const [checkout, setCheckout] = React.useState(false);

  React.useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/wholesale-catalog/products`, { headers: getAuthHeaders() });
        if (r.status === 403 || r.status === 401) { setState('denied'); return; }
        const d = await r.json();
        setProducts(d.products || []);
        setCounts(d.category_counts || {});
        setCategories(d.categories || []);
        setState('ready');
      } catch { setState('denied'); }
    };
    load();
  }, [getAuthHeaders]);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) return c.map((i) => i.id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...c, { id: p.id, name: p.name, code: p.code, price_per_case: p.price_per_case, strength_mg: p.strength_mg, vials_per_case: p.vials_per_case, quantity: 1 }];
    });
    toast.success(`${p.name} added`);
  };
  const setQty = (id, q) => setCart((c) => q <= 0 ? c.filter((i) => i.id !== id) : c.map((i) => i.id === id ? { ...i, quantity: q } : i));
  const cartTotal = cart.reduce((s, i) => s + i.price_per_case * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  let list = products.filter((p) => (activeCat === 'all' || p.category === activeCat));
  if (availOnly) list = list.filter((p) => p.available);
  if (search) { const q = search.toLowerCase(); list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q)); }
  if (sort === 'price-asc') list = [...list].sort((a, b) => a.price_per_case - b.price_per_case);
  else if (sort === 'price-desc') list = [...list].sort((a, b) => b.price_per_case - a.price_per_case);
  else if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));

  if (state === 'loading') return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading catalog…</div>;
  if (state === 'denied') return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="catalog-denied">
      <div className="text-center max-w-md">
        <Lock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Wholesale access required</h1>
        <p className="text-slate-500 mb-6">This catalog is available to approved wholesale accounts only. Contact us if you'd like wholesale access.</p>
        <Button onClick={() => navigate('/')} className="bg-teal-700 hover:bg-teal-800 text-white">Back to home</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="catalog-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Catalog</h1>
            <p className="text-slate-500 text-sm">Live wholesale inventory — submit requests for review.</p>
          </div>
          <Button variant="outline" onClick={() => setCartOpen(true)} className="relative" data-testid="catalog-cart-btn">
            <ShoppingCart className="h-4 w-4 mr-2" /> Cart
            {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-teal-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{cartCount}</span>}
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="hidden lg:block w-56 shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <p className="text-xs uppercase text-slate-400 font-semibold mb-3">Category</p>
              <button onClick={() => setActiveCat('all')} className={`w-full flex justify-between items-center px-2 py-1.5 rounded text-sm ${activeCat === 'all' ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="cat-all">
                <span>All products</span><span className="text-slate-400">{products.length}</span>
              </button>
              {categories.map((c) => (
                <button key={c} onClick={() => setActiveCat(c)} className={`w-full flex justify-between items-center px-2 py-1.5 rounded text-sm ${activeCat === c ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`cat-${c}`}>
                  <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: CAT_COLORS[c] || '#94a3b8' }} />{c}</span>
                  <span className="text-slate-400">{counts[c] || 0}</span>
                </button>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs uppercase text-slate-400 font-semibold mb-3">Availability</p>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={availOnly} onChange={(e) => setAvailOnly(e.target.checked)} data-testid="avail-only" /> Show available only
              </label>
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search peptides, codes, SKUs…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="catalog-search" />
            </div>
            <select className="border rounded-md h-9 px-3 text-sm mb-4 bg-white" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort">
              <option value="featured">Sort: Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name">Name: A–Z</option>
            </select>

            {list.length === 0 ? (
              <div className="text-center py-16 text-slate-400"><PackageSearch className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No products match.</p></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {list.map((p) => (
                  <div key={p.id} className={`bg-white rounded-xl border border-slate-200 p-4 flex flex-col ${!p.available ? 'opacity-70' : ''}`} data-testid={`catalog-card-${p.id}`}>
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide mb-2 px-2 py-0.5 rounded self-start" style={{ background: (CAT_COLORS[p.category] || '#64748b') + '22', color: CAT_COLORS[p.category] || '#64748b' }}>{p.category}</span>
                    <div className="h-24 flex items-center justify-center mb-2">
                      <CatalogImage product={p} />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm leading-tight">{p.name}</h3>
                    <p className="text-xs text-slate-500 mb-2">{p.strength_mg}{p.vials_per_case ? ` · ${p.vials_per_case} vials/case` : ''}</p>
                    <p className="text-[11px] text-slate-400 font-mono mb-3">{p.code}</p>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <span className="font-bold text-slate-900">${p.price_per_case.toFixed(2)}<span className="text-xs font-normal text-slate-400">/case</span></span>
                      {p.available ? (
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => addToCart(p)} data-testid={`add-to-cart-${p.id}`}>Add to cart</Button>
                      ) : (
                        <span className="text-xs text-slate-400">Out of stock</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {cartOpen && (
        <CartDrawer cart={cart} setQty={setQty} total={cartTotal} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckout(true); }} />
      )}
      {checkout && (
        <CheckoutModal cart={cart} total={cartTotal} user={user} getAuthHeaders={getAuthHeaders} onClose={() => setCheckout(false)} onDone={() => { setCart([]); setCheckout(false); navigate('/dashboard'); }} />
      )}
    </div>
  );
};

const CartDrawer = ({ cart, setQty, total, onClose, onCheckout }) => (
  <div className="fixed inset-0 z-50 flex justify-end" data-testid="catalog-cart-drawer">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="relative w-full max-w-md bg-white h-full p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold">Wholesale Cart</h3>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      {cart.length === 0 ? <p className="text-slate-400 text-sm">Your cart is empty.</p> : (
        <div className="space-y-3">
          {cart.map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3" data-testid={`cart-line-${i.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{i.name}</p>
                <p className="text-xs text-slate-500">${i.price_per_case.toFixed(2)}/case · {i.code}</p>
              </div>
              <div className="flex items-center gap-1">
                <button className="h-7 w-7 border rounded" onClick={() => setQty(i.id, i.quantity - 1)}>−</button>
                <span className="w-8 text-center text-sm">{i.quantity}</span>
                <button className="h-7 w-7 border rounded" onClick={() => setQty(i.id, i.quantity + 1)}>+</button>
              </div>
            </div>
          ))}
          <div className="flex justify-between font-bold pt-2"><span>Estimated total</span><span>${total.toFixed(2)}</span></div>
          <p className="text-xs text-slate-400">Shipping/handling confirmed on your invoice after review.</p>
          <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white" onClick={onCheckout} data-testid="catalog-checkout-btn">Submit request for review</Button>
        </div>
      )}
    </div>
  </div>
);

const CheckoutModal = ({ cart, total, user, getAuthHeaders, onClose, onDone }) => {
  const [method, setMethod] = React.useState('zelle');
  const [addr, setAddr] = React.useState({ name: user?.name || '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '' });
  const [phone, setPhone] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    for (const k of ['name', 'address_line1', 'city', 'state', 'zip_code']) {
      if (!addr[k].trim()) { toast.error('Please complete the shipping address'); return; }
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/wholesale-catalog/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ items: cart.map((i) => ({ id: i.id, quantity: i.quantity })), payment_method: method, shipping_address: addr, phone, notes }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Request failed');
      toast.success('Request submitted! We\'ll review and send your invoice.');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="catalog-checkout-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-slate-900">Submit wholesale request</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Estimated total <strong>${total.toFixed(2)}</strong> ({cart.length} items). No payment now — we review and send an invoice via your chosen method.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Payment method</Label>
            <div className="flex gap-2 mt-1">
              {['zelle', 'cash_app'].map((m) => (
                <button key={m} type="button" onClick={() => setMethod(m)} className={`flex-1 border rounded-lg py-2 text-sm font-medium ${method === m ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`} data-testid={`checkout-method-${m}`}>{m === 'zelle' ? 'Zelle' : 'Cash App'}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Recipient name *</Label><Input value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="checkout-name" /></div>
          <div><Label className="text-xs">Address line 1 *</Label><Input value={addr.address_line1} onChange={(e) => setAddr({ ...addr, address_line1: e.target.value })} data-testid="checkout-addr1" /></div>
          <div><Label className="text-xs">Address line 2</Label><Input value={addr.address_line2} onChange={(e) => setAddr({ ...addr, address_line2: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">City *</Label><Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="checkout-city" /></div>
            <div><Label className="text-xs">State *</Label><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="checkout-state" /></div>
            <div><Label className="text-xs">ZIP *</Label><Input value={addr.zip_code} onChange={(e) => setAddr({ ...addr, zip_code: e.target.value })} data-testid="checkout-zip" /></div>
          </div>
          <div><Label className="text-xs">Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone" /></div>
          <div><Label className="text-xs">Notes (optional)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-teal-700 hover:bg-teal-800 text-white" disabled={submitting} onClick={submit} data-testid="checkout-submit">{submitting ? 'Submitting…' : 'Submit request'}</Button>
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;
